import { useIsMobile } from "@universe/ui/hooks/use-mobile";
import { cn } from "@universe/ui/lib/utils";
import { useTranslations } from "next-intl";

import CurrencyFlag from "@/components/currency-flag";
import {
  PAYMENT_CANCELLATION_REASON,
  WITHDRAWAL_REASON_PAYMENT_REVERSAL,
  WITHDRAWAL_REASON_WITHDRAWAL_REVERSAL,
} from "@/constants/ledgerReasons";
import {
  getBeneficiaryDisplayName,
  getLegalEntityName,
} from "@/utils/getLegalEntityName";
import { FilteredBeneficiaryDetail } from "@generated/types";

import LedgerDirectionIcon from "./ledger-direction-icon";
import TransactionStatusBadge, {
  TransactionStatusBadgeType,
} from "./transaction-status-badge";
import { JournalType } from "./types";

interface LedgerEntryListBasicInfoProps {
  entry: LedgerEntryResolvedType | null;
  showSubtitle?: boolean;
  className?: string;
  truncate?: boolean;
  isHeader?: boolean;
}

type LedgerEntryItemDetails = {
  title: string;
  subtitle?: string;
  status?: TransactionStatusBadgeType;
  isReturned?: boolean;
};

type TranslationFunctions = {
  tLedgerReasons: ReturnType<typeof useTranslations>;
  tLedgerFallback: ReturnType<typeof useTranslations>;
  tLedgerTransactions: ReturnType<typeof useTranslations>;
  isMobile: boolean;
};

const getLedgerEntryItemDetails = (
  entry: LedgerEntryResolvedType | null,
  translations: TranslationFunctions
) => {
  if (!entry) return null;

  switch (entry.journal) {
    case JournalType.BLOCKCHAIN_WITHDRAWALS:
      return blockchainWithdrawalDetails(
        entry as LedgerEntryResolvedBlockchainWithdrawal,
        translations
      );
    case JournalType.BLOCKCHAIN_TRANSACTIONS:
      return blockchainTransactionDetails(
        entry as LedgerEntryResolvedBlockchainTransactions,
        translations
      );
    case JournalType.FX_TICKETS:
      return fxTicketDetails(
        entry as LedgerEntryResolvedFxTicket,
        translations
      );
    case JournalType.PAYMENTS:
      return paymentDetails(entry as LedgerEntryResolvedPayments, translations);
    case JournalType.BANK_TRANSACTIONS:
      return bankingTransactionDetails(
        entry as LedgerEntryResolvedBankTransactions,
        translations
      );
    case JournalType.POSTINGS:
      return postingsDetails(entry, translations);
  }
};

const blockchainTransactionDetails = (
  entry: LedgerEntryResolvedBlockchainTransactions,
  { tLedgerReasons, tLedgerFallback }: TranslationFunctions
): LedgerEntryItemDetails | null => {
  if (!entry) return null;

  const isCredit = entry.reason === "credit";
  const isDebit = entry.reason === "debit";
  const isCreditDeduction = entry.reason === "credit-deduction";
  const isDebitRebate = entry.reason === "debit-rebate";
  const isCreditRebate = entry.reason === "credit-rebate";
  const isDebitDeduction = entry.reason === "debit-deduction";

  // BFF returns resolvedData=null when upstream resolution fails. Render the
  // row using only base ledger fields (product, reason, comment).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rd = entry.resolvedData as any;

  return {
    title:
      (isCredit && tLedgerFallback("received", { product: entry.product })) ||
      (isDebit &&
        (getBeneficiaryDisplayName(
          rd?.resolvedBlockchainWithdrawal?.beneficiary
        ) ||
          tLedgerFallback("sent", { product: entry.product }))) ||
      (isCreditDeduction &&
        tLedgerFallback("deduction", { product: entry.product })) ||
      (isDebitRebate && entry.comment) ||
      (isCreditRebate && entry.comment) ||
      (isDebitDeduction && entry.comment) ||
      "",
    // Dynamic API reason: only translate when a ledger.reasons entry exists.
    // Absent values (e.g. "pending-payment") fall through to the component's
    // generic-label fallback chain below and must NOT trip the i18n missing-key
    // watchdog (ledger.reasons has no `.default` sibling for it to recognise).
    subtitle:
      entry.reason && tLedgerReasons.has(entry.reason)
        ? tLedgerReasons(entry.reason)
        : "",
    status: rd?.transactionState as TransactionStatusBadgeType | undefined,
  };
};

const bankingTransactionDetails = (
  entry: LedgerEntryResolvedBankTransactions,
  { tLedgerReasons, tLedgerFallback }: TranslationFunctions
): LedgerEntryItemDetails | null => {
  if (!entry) return null;

  // BFF returns resolvedData=null when upstream resolution fails. Fall back to
  // the journal-direction translation so the row still renders (just without
  // beneficiary/account-name detail).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rd = entry.resolvedData as any;

  const reasonObject = {
    debit: {
      title:
        getLegalEntityName(
          rd?.resolvedRelatedPayment?.beneficiary as FilteredBeneficiaryDetail
        ) || tLedgerFallback("sent", { product: entry.product }),
      subtitle: tLedgerReasons("debit"),
    },
    credit: {
      title:
        (rd?.debitBankAccount as { accountName?: string })?.accountName ||
        tLedgerFallback("received", { product: entry.product }),
      fallbackTitle: "",
      subtitle: tLedgerReasons("credit"),
    },
    "debit-rebate": {
      title: tLedgerFallback("received", { product: entry.product }),
      fallbackTitle: "",
      subtitle: tLedgerReasons("debit-rebate"),
    },
    "credit-rebate": {
      title: entry.comment,
      fallbackTitle: "",
      subtitle: tLedgerReasons("credit-rebate"),
    },
    "credit-deduction": {
      title: entry.comment,
      fallbackTitle: "",
      subtitle: tLedgerReasons("credit-deduction"),
    },
    "debit-deduction": {
      title: entry.comment,
      fallbackTitle: "",
      subtitle: tLedgerReasons("debit-deduction"),
    },
  };

  return {
    title: reasonObject[entry.reason as keyof typeof reasonObject]?.title ?? "",
    subtitle: reasonObject[entry.reason as keyof typeof reasonObject]?.subtitle,
    status: rd?.transactionState as TransactionStatusBadgeType | undefined,
  };
};

const paymentDetails = (
  entry: LedgerEntryResolvedPayments,
  { tLedgerReasons, tLedgerFallback }: TranslationFunctions
): LedgerEntryItemDetails | null => {
  if (!entry) return null;

  // BFF returns resolvedData=null when upstream resolution fails. Fall back to
  // the generic "Sent <product>" label and omit the status badge.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rd = entry.resolvedData as any;

  return {
    title:
      getLegalEntityName(rd?.beneficiary as FilteredBeneficiaryDetail) ||
      tLedgerFallback("sent", { product: entry.product }),
    // See note in blockchainTransactionDetails: guard dynamic reason lookups so
    // an expected-absent reason resolves to "" without a watchdog event.
    subtitle:
      entry.reason && tLedgerReasons.has(entry.reason)
        ? tLedgerReasons(entry.reason)
        : "",
    status: rd?.paymentState as TransactionStatusBadgeType | undefined,
    isReturned:
      entry.reason === WITHDRAWAL_REASON_PAYMENT_REVERSAL ||
      (entry.balance &&
        entry.balance > 0 &&
        entry.reason === PAYMENT_CANCELLATION_REASON)
        ? true
        : undefined,
  };
};

const blockchainWithdrawalDetails = (
  entry: LedgerEntryResolvedBlockchainWithdrawal,
  { tLedgerReasons, tLedgerFallback }: TranslationFunctions
): LedgerEntryItemDetails | null => {
  if (!entry) return null;

  // Determine subtitle based on reason
  const subtitle =
    entry.reason === "fee"
      ? tLedgerReasons("fee")
      : entry.reason === "pending-withdrawal"
        ? tLedgerReasons("debit")
        : tLedgerReasons("debit"); // Default to "Payment" for regular withdrawals

  return {
    title:
      getBeneficiaryDisplayName(entry.resolvedData?.beneficiary) ||
      tLedgerFallback("sent", { product: entry.product }),
    subtitle,
    status: entry.resolvedData?.withdrawalState as
      | TransactionStatusBadgeType
      | undefined,
    isReturned: entry.reason === WITHDRAWAL_REASON_WITHDRAWAL_REVERSAL,
  };
};

const fxTicketDetails = (
  entry: LedgerEntryResolvedFxTicket,
  { tLedgerTransactions, isMobile }: TranslationFunctions
): LedgerEntryItemDetails | null => {
  if (!entry) return null;

  // Handle both flattened and wrapped (data array) resolvedData shapes
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rd = entry.resolvedData as any;
  const tradeData = rd?.data?.[0] ?? rd;

  // Determine "from" and "to" based on balance direction
  // "From" = currency with negative balance (spent)
  // "To" = currency with positive balance (received)
  const { baseCurrency, counterCurrency } = tradeData;
  const isCredit = (entry.balance ?? 0) > 0;

  // Get the other currency (the one that's not this entry's product)
  const otherCurrency =
    entry.product === baseCurrency ? counterCurrency : baseCurrency;

  // If credit (positive balance), this product was received ("to"), other was spent ("from")
  // If debit (negative balance), this product was spent ("from"), other was received ("to")
  const from = isCredit ? otherCurrency : entry.product;
  const to = isCredit ? entry.product : otherCurrency;

  return {
    title: tLedgerTransactions("exchange", {
      from: from ?? "",
      to: to ?? "",
    }),
    subtitle: isMobile
      ? tLedgerTransactions("fxExchangeMobile")
      : tLedgerTransactions("fxExchange"),
    status: tradeData?.settled
      ? ("RECONCILED" as TransactionStatusBadgeType)
      : ("PENDING" as TransactionStatusBadgeType),
  };
};

const postingsDetails = (
  entry: LedgerEntryResolvedType | null,
  { tLedgerFallback }: TranslationFunctions
): LedgerEntryItemDetails | null => {
  if (!entry) return null;

  return {
    title: entry.comment || tLedgerFallback("other"),
    subtitle: tLedgerFallback("other"),
  };
};

export default function LedgerEntryListBasicInfo({
  entry,
  showSubtitle = true,
  className,
  truncate = true,
  isHeader = false,
}: LedgerEntryListBasicInfoProps) {
  const tLedgerReasons = useTranslations("ledger.reasons");
  const tLedgerFallback = useTranslations("ledger.fallback");
  const isMobile = useIsMobile();
  const tLedgerTransactions = useTranslations("ledger.transaction");

  const translations: TranslationFunctions = {
    tLedgerReasons,
    tLedgerFallback,
    tLedgerTransactions,
    isMobile,
  };

  const resolvedEntry = getLedgerEntryItemDetails(entry, translations);

  if (!entry || !resolvedEntry) return null;

  const isReconciled =
    resolvedEntry.status === "RECONCILED" ||
    resolvedEntry.status === "COMMITTED" ||
    entry.journal === JournalType.POSTINGS;

  const isBlockchainFee =
    entry.reason === "fee" &&
    entry.journal === JournalType.BLOCKCHAIN_WITHDRAWALS;

  const isBlockchainWithdrawal =
    entry.journal === JournalType.BLOCKCHAIN_WITHDRAWALS &&
    entry.reason === "pending-withdrawal";

  const isPendingPayment =
    entry.journal === JournalType.PAYMENTS &&
    entry.reason === "pending-payment";

  // Determine subtitle text - always show subtitle with fallback
  let displaySubtitle: string;

  if (resolvedEntry?.subtitle && resolvedEntry.subtitle.trim() !== "") {
    displaySubtitle = resolvedEntry.subtitle;
  } else if (isPendingPayment) {
    displaySubtitle = tLedgerReasons("debit");
  } else if (isBlockchainWithdrawal) {
    displaySubtitle = tLedgerReasons("debit");
  } else if (isBlockchainFee) {
    displaySubtitle = tLedgerReasons("fee");
  } else if (entry.journal === JournalType.PAYMENTS) {
    displaySubtitle = tLedgerReasons("debit");
  } else if (entry.journal === JournalType.BLOCKCHAIN_WITHDRAWALS) {
    displaySubtitle = tLedgerReasons("debit");
  } else if (entry.journal === JournalType.BLOCKCHAIN_TRANSACTIONS) {
    displaySubtitle =
      entry.reason === "credit"
        ? tLedgerReasons("credit")
        : tLedgerReasons("debit");
  } else if (entry.journal === JournalType.BANK_TRANSACTIONS) {
    displaySubtitle =
      entry.reason === "credit"
        ? tLedgerReasons("credit")
        : tLedgerReasons("debit");
  } else {
    displaySubtitle = tLedgerFallback("other");
  }

  // Extract remittanceInformation based on journal type (only for list items, not headers)
  const remittanceInformation =
    !isHeader && entry.journal === JournalType.PAYMENTS
      ? (entry.resolvedData as { remittanceInformation?: string })
          ?.remittanceInformation
      : !isHeader && entry.journal === JournalType.BANK_TRANSACTIONS
        ? entry.reason === "credit"
          ? (entry.resolvedData as { narrative?: string[] })?.narrative?.[0]
          : (
              entry.resolvedData as {
                resolvedRelatedPayment?: { remittanceInformation?: string };
              }
            )?.resolvedRelatedPayment?.remittanceInformation
        : undefined;

  return (
    <div className={cn("flex items-center gap-1.5 overflow-hidden", className)}>
      {/* Mobile stacked layout (< md) - only for list items, not headers */}
      {!isHeader && (
        <div className="relative mr-1.5 md:hidden">
          <CurrencyFlag size="28px" currency={entry.product} />
          <div
            className={cn(
              "absolute -bottom-1 -right-2 rounded-full bg-[radial-gradient(circle,white_0%,oklch(from_var(--brand-primary)_l_c_h_/_0.15)_100%)]",
              entry.journal === JournalType.FX_TICKETS && "p-0.5"
            )}
          >
            <LedgerDirectionIcon
              journalType={entry.journal as JournalType}
              balance={entry.balance as number}
              isFee={
                entry.reason === "credit-deduction" || entry.reason === "fee"
              }
              className={cn(
                "translate-x-0 scale-100",
                entry.journal === JournalType.FX_TICKETS ? "w-4 h-4" : "w-5 h-5"
              )}
            />
          </div>
        </div>
      )}

      {/* Desktop side-by-side layout (md+) or header layout (all breakpoints) */}
      <div className={cn(isHeader ? "contents" : "hidden md:contents")}>
        <LedgerDirectionIcon
          journalType={entry.journal as JournalType}
          balance={entry.balance as number}
          isFee={entry.reason === "credit-deduction" || entry.reason === "fee"}
          className={cn(isHeader && "lg:h-7 lg:w-7")}
        />
        <div className="mr-1.5">
          <CurrencyFlag
            size={isHeader && !isMobile ? "26px" : "24px"}
            currency={entry.product}
          />
        </div>
      </div>
      {/* In a list row the title column fills the row beside the flag below
          md, and its `contain:inline-size` keeps the nowrap title out of the
          row's intrinsic width, so the title truncates instead of widening
          a flex ancestor without `min-w-0`, such as a story's root, past the
          screen. In the app the content column already clips the row below
          md. From md up `ledger-entry-item.tsx` contains the whole info side
          instead. The title shrinks to an ellipsis beside the amount only if
          every box between this root and the `<p>` has `min-w-0`: from md the
          column, which sizes to its title, and from xl also the two wrappers
          inside it, where the column lays the title, remittance, subtitle and
          badge out in one line. Without them the title keeps its full width,
          so this root's `overflow-hidden` cuts it with no ellipsis, and from
          xl it also pushes the subtitle and badge out of the row. */}
      <div
        className={cn(
          "flex flex-col xl:flex-row xl:gap-2",
          !isHeader &&
            "min-w-0 flex-1 [contain:inline-size] md:flex-initial md:[contain:none]"
        )}
      >
        {/* Desktop: Title (remittanceInformation) • Payment (Status badge) */}
        {/* Mobile: Title on line 1, remittanceInformation • Payment on line 2, Status badge on line 3 */}
        <div
          className={cn(
            "flex flex-col xl:flex-row xl:items-center gap-0 xl:gap-2",
            !isHeader && "min-w-0"
          )}
        >
          {/* Title */}
          <div className={cn("flex items-center", !isHeader && "min-w-0")}>
            <p
              className={cn(
                "font-medium text-base md:text-lg -ml-[1px] xl:ml-0",
                truncate && "truncate",
                // A list row's title is held only by its row (see the title
                // column above). The drawer header keeps its viewport caps.
                truncate &&
                  isHeader &&
                  "max-w-[calc(100vw-250px)] lg:max-w-[calc(100vw-700px)] xl:max-w-[calc(100vw-850px)]",
                isHeader &&
                  "max-w-[calc(100vw-150px)] sm:w-[330px] lg:w-[350px]",
                !showSubtitle && "text-base md:text-lg font-medium"
              )}
            >
              {resolvedEntry?.title}
            </p>
          </div>

          {/* Desktop: (remittanceInformation) • */}
          {!isHeader && remittanceInformation && (
            <>
              <span className="text-sm md:text-sm text-gray-700 hidden xl:inline">
                ({remittanceInformation})
              </span>
              <span className="text-gray-800 text-xl xl:text-2xl hidden xl:block">
                •
              </span>
            </>
          )}

          {/* Desktop: • (when no remittanceInformation) */}
          {!isHeader && !remittanceInformation && (
            <span className="text-gray-800 text-xl xl:text-2xl hidden xl:block">
              •
            </span>
          )}

          {/* Transaction type and status badge section - always show subtitle (except for headers) */}
          {!isHeader && (
            <div className="flex flex-col xl:flex-row xl:items-center gap-1">
              {/* Mobile: remittanceInformation • Payment */}
              <div className="flex flex-row items-center gap-1.5 xl:gap-1">
                {remittanceInformation && (
                  <>
                    <span className="text-sm md:text-sm text-gray-700 xl:hidden">
                      ({remittanceInformation})
                    </span>
                    <span className="text-gray-800 text-xl xl:hidden">•</span>
                  </>
                )}
                <span className="text-sm md:text-sm text-gray-700">
                  {displaySubtitle}
                </span>
              </div>
              {/* Status badge - always show after subtitle */}
              {!isReconciled && resolvedEntry.status && (
                <div className="w-fit xl:contents">
                  <TransactionStatusBadge
                    status={resolvedEntry.status as TransactionStatusBadgeType}
                    isReturned={resolvedEntry.isReturned}
                    className="-ml-[3px] xl:ml-1"
                    size="xs"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
